import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LinePlusCircleIcon from 'LuxKit/Icons/line/LinePlusCircleIcon'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LineCalendarPlusIcon from 'LuxKit/Icons/line/LineCalendarPlusIcon'
import useRebrandVersion from 'hooks/useRebrandVersion'
import noop from 'lib/function/noop'

// The attach IconButton is 40px wide with a centred 20px glyph and uses
// horizontalOutdent="start" (a visual translate) to sit that glyph flush with the
// composer's content edge. Neither the transform nor the button's 10px of trailing
// dead space is reclaimed by the grid track / flex row, so the glyph would otherwise
// sit ~28px from the textarea. Pull the following control back to leave a 12px gap —
// matching the textarea-to-mic gap on the trailing edge (8px row gap + 4px of dead
// space beside the 20px mic glyph in its 28px button).
const ATTACH_BUTTON_GLYPH_TRIM = 16

const AttachmentMenuAnchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-inline-end: ${rem(-ATTACH_BUTTON_GLYPH_TRIM)};
`

const FloatingAttachmentMenu = styled.div`
  position: absolute;
  inset-inline-start: 0;
  inset-block-end: calc(100% + ${rem(8)});
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(8)};
  z-index: 3;
`

const FloatingAssistChip = styled(AssistChip)`
  && {
    --chip-stroke-width: 0;
    --chip-stroke-color: transparent;
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    box-shadow: ${props => props.theme.shadow.bottom.small};

    svg {
      color: currentColor;
    }

    &:not(:disabled):active {
      background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
      color: ${props => props.theme.palette.neutral.surface.darkContrast};
    }
  }
`

const UPLOAD_ICON = <LinePaperclipIcon size="S" />
const ADD_BOOKING_ICON = <LineCalendarPlusIcon size="S" />
const ATTACH_MENU_ICON = <LinePlusCircleIcon />

interface Props {
  onUploadClick: () => void
  onAddBookingClick?: () => void
  showAddBookingAction?: boolean
  disabled?: boolean
}

/** The composer's ⊕ button and the menu of attach actions it opens. */
function ChatComposerAttachMenu({
  onUploadClick,
  onAddBookingClick = noop,
  showAddBookingAction = true,
  disabled = false,
}: Props) {
  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!(event.target instanceof Node)) {
        return
      }

      if (anchorRef.current?.contains(event.target)) {
        return
      }

      setIsOpen(false)
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  useEffect(() => {
    if (disabled) {
      setIsOpen(false)
    }
  }, [disabled])

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const handleUploadClick = useCallback(() => {
    setIsOpen(false)
    onUploadClick()
  }, [onUploadClick])

  const handleAddBookingClick = useCallback(() => {
    setIsOpen(false)
    onAddBookingClick()
  }, [onAddBookingClick])

  return (
    <AttachmentMenuAnchor ref={anchorRef}>
      {isOpen && (
        <FloatingAttachmentMenu>
          <FloatingAssistChip
            variant="filled"
            size="large"
            onClick={handleUploadClick}
            startIcon={UPLOAD_ICON}
          >
            {intl.formatMessage({
              id: 'xFhyB-33Xsfg5y01N-wj',
              defaultMessage: 'Upload a photo or file',
              description: 'Assist chip label shown above TripPlanner chat input to open the existing file upload picker',
            })}
          </FloatingAssistChip>
          {showAddBookingAction && (
            <FloatingAssistChip
              variant="filled"
              size="large"
              onClick={handleAddBookingClick}
              startIcon={ADD_BOOKING_ICON}
            >
              {intl.formatMessage({
                id: 'N3DmOda7OJ9ekDka-zc2',
                defaultMessage: 'Add a booking',
                description: 'Assist chip label shown above TripPlanner chat input for add booking action placeholder',
              })}
            </FloatingAssistChip>
          )}
        </FloatingAttachmentMenu>
      )}
      <IconButton
        kind={isRebrand ? 'primary' : 'tertiary'}
        variant="dark"
        shape="circle"
        size="medium"
        horizontalOutdent="start"
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-label={intl.formatMessage({
          id: 'V6LhP1J1ebLGBTDknit8',
          defaultMessage: 'Attach files and more',
          description: 'Aria-label for the attach files button in the chat message input',
        })}
      >
        {ATTACH_MENU_ICON}
      </IconButton>
    </AttachmentMenuAnchor>
  )
}

export default ChatComposerAttachMenu
